/* PastéBCN — Header
   - Toggle .is-scrolled cuando scrollY > 20.
   - Sticky reveal-on-scroll SOLO en la home (.template-index) usando
     el patrón Headroom: top (estático en flujo), pinned (fixed visible),
     unpinned (fixed oculto). Un spacer hermano evita el salto.
   - Focus trap dentro del drawer mobile mientras está abierto.
   - ESC cierra el drawer. */

if (!customElements.get('pb-header')) {
  class PbHeader extends HTMLElement {
    connectedCallback() {
      this.shadowThreshold = 20;
      this.topThreshold = 10;
      this.revealDelta = 5;
      this.scrolledStateEnabled = this.dataset.scrolledState !== 'false';
      this.stickyOnHome = this.dataset.stickyOnHome === 'true';
      this.isHome = document.body.classList.contains('template-index');
      // La ficha de producto también revela el header al subir: mantiene el
      // carrito alcanzable en fichas largas y da destino visible al
      // fly-to-cart. Mismo patrón Headroom, mismo spacer.
      this.isProduct = document.body.classList.contains('template-product');
      this.revealEnabled = this.stickyOnHome && (this.isHome || this.isProduct);

      this._lastScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      this._ticking = false;
      this._currentState = null;
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);

      if (this.revealEnabled) {
        this._setupSpacer();
      }

      if (this.scrolledStateEnabled || this.revealEnabled) {
        window.addEventListener('scroll', this._onScroll, { passive: true });
        this._updateState();
      }

      document.addEventListener('keydown', this._onKeyDown);

      this._wireDrawer();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('keydown', this._onKeyDown);
      if (this._spacer && this._spacer.parentNode) {
        this._spacer.parentNode.removeChild(this._spacer);
      }
    }

    _setupSpacer() {
      this._spacer = document.createElement('div');
      this._spacer.className = 'pb-header-spacer';
      this._spacer.setAttribute('aria-hidden', 'true');
      this.parentNode.insertBefore(this._spacer, this.nextSibling);
      this._syncSpacerHeight();
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    _syncSpacerHeight() {
      if (!this._spacer) return;
      this._spacer.style.height = this.offsetHeight + 'px';
    }

    _onResize() {
      this._syncSpacerHeight();
    }

    _applyState(state) {
      if (state === this._currentState) return;
      this.classList.remove('pb-header--top', 'pb-header--pinned', 'pb-header--unpinned');
      this.classList.add(state);
      if (this._spacer) {
        this._spacer.style.display = state === 'pb-header--top' ? 'none' : 'block';
      }
      this._currentState = state;
    }

    _onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      window.requestAnimationFrame(() => {
        this._updateState();
        this._ticking = false;
      });
    }

    _updateState() {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;

      if (this.scrolledStateEnabled) {
        this.classList.toggle('is-scrolled', scrollY > this.shadowThreshold);
      }

      if (this.revealEnabled) {
        if (scrollY <= this.topThreshold) {
          this._applyState('pb-header--top');
        } else if (Math.abs(scrollY - this._lastScrollY) >= this.revealDelta) {
          if (scrollY > this._lastScrollY) {
            this._applyState('pb-header--unpinned');
          } else {
            this._applyState('pb-header--pinned');
          }
        }
      }

      this._lastScrollY = scrollY;
    }

    _onKeyDown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      const drawer = this.querySelector('header-drawer details[open]');
      if (drawer) {
        const summary = drawer.querySelector('summary');
        if (summary) summary.click();
      }
    }

    _wireDrawer() {
      const details = this.querySelector('header-drawer details');
      if (!details) return;
      const drawerPanel = details.querySelector('.pb-drawer');
      if (!drawerPanel) return;

      const closeDrawer = () => details.removeAttribute('open');

      const closeButton = drawerPanel.querySelector('.pb-drawer__close');
      if (closeButton) {
        closeButton.addEventListener('click', closeDrawer);
      }

      const overlay = this.querySelector('header-drawer .pb-drawer__overlay');
      if (overlay) {
        overlay.addEventListener('click', closeDrawer);
      }

      const observer = new MutationObserver(() => {
        const isOpen = details.hasAttribute('open');
        document.body.classList.toggle('pb-drawer-open', isOpen);
        if (isOpen) {
          this._trapFocus(drawerPanel);
        } else {
          this._releaseFocus();
        }
      });
      observer.observe(details, { attributes: true, attributeFilter: ['open'] });
      this._observer = observer;
    }

    _trapFocus(panel) {
      this._lastActive = document.activeElement;
      this._focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      this._focusHandler = (event) => {
        if (event.key !== 'Tab' || !this._focusables || this._focusables.length === 0) return;
        const first = this._focusables[0];
        const last = this._focusables[this._focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this._focusHandler);
    }

    _releaseFocus() {
      if (this._focusHandler) {
        document.removeEventListener('keydown', this._focusHandler);
        this._focusHandler = null;
      }
      if (this._lastActive && typeof this._lastActive.focus === 'function') {
        this._lastActive.focus();
        this._lastActive = null;
      }
    }
  }

  customElements.define('pb-header', PbHeader);
}

/* PastéBCN — Search wildcard on submit
   Antes de enviar el form, si la query tiene 3-6 chars sin espacios y sin "*",
   añade "*" al final para que Shopify haga prefix-match (tolera typos cortos).
   El input visible se restaura tras el submit para que el usuario nunca vea el "*". */
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!form || !form.matches || !form.matches('[data-pb-search]')) return;
  const input = form.querySelector('input[name="q"]');
  if (!input) return;
  const value = (input.value || '').trim();
  if (!value) return;
  if (value.length >= 3 && value.length <= 6 && !value.includes(' ') && !value.endsWith('*')) {
    input.value = value + '*';
    setTimeout(() => {
      input.value = value;
    }, 0);
  }
});
